/**
 * Right-side slide-in drawer for one rehab project. Built to mirror the
 * `aside.drawer` block in design/Syntra/rehab.html section-for-section:
 *
 *   1. Header — thumbnail · address title/sub · status & strategy chips ·
 *      close affordance.
 *   2. Progress overview — large header pct + linear bar.
 *   3. Key stats grid — Days · Budget Used · Remaining · Daily Burn.
 *   4. Budget vs Actual chart — total summary bar + per-task bars.
 *   5. Scope & tasks — checklist with budget/actual columns + add-custom flow.
 *   6. Notes — large textarea, autosaved on blur.
 *   7. Footer — status-conditional primary action.
 *
 * Source of truth for scope and tasks is the `rehab_line_items` table:
 *   - `category = 'standard' | 'custom'` → scope vs custom row
 *   - `status = 'completed'`              → done flag
 *   - `estimated_cost`                    → budget for the row
 *   - `actual_cost`                       → money spent against the row, which
 *                                           counts toward Budget Used only
 *                                           once the row is completed
 *
 * Notes, start/complete dates, and the project itself still live on
 * `rehab_projects` and route through `useRehab`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Hammer, NotebookPen, Play, TrendingUp, X } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useDealSettings } from "@/hooks/use-deal-settings";
import { useRehab, type RehabProject } from "@/hooks/use-rehab";
import { useRentals } from "@/hooks/use-rentals";

import { RehabBudgetChart } from "./RehabBudgetChart";
import { RehabScopeSection } from "./RehabScopeSection";
import { RehabStatsGrid } from "./RehabStatsGrid";
import { composeScopeItems, type ScopeItem, visibleScopeItems } from "./rehab-scope";
import { deriveStatus, STATUS_LABEL, type RehabStatus } from "./rehab-status";

interface RehabDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: RehabProject | null;
  /** Update rehab_projects.notes (and other project fields). */
  updateProject: (id: string, updates: Partial<RehabProject>) => Promise<void>;
  startProject: (id: string) => Promise<void>;
  completeProject: (id: string) => Promise<void>;
  /** Refetch the page list when project-level fields change. */
  refresh: () => void;
}

const STATUS_CHIP: Record<RehabStatus, string> = {
  not_started: "bg-muted text-muted-foreground border border-border",
  in_progress: "bg-warning/15 text-warning",
  completed: "bg-success/15 text-success",
};

const STATUS_HEADLINE: Record<RehabStatus, string> = {
  not_started: "Not started yet",
  in_progress: "In progress",
  completed: "Rehab completed",
};

export function RehabDetailDrawer({
  open,
  onOpenChange,
  project,
  updateProject,
  startProject,
  completeProject,
  refresh,
}: RehabDetailDrawerProps) {
  const queryClient = useQueryClient();
  const { listings } = useRentals();
  const [rentedBlockOpen, setRentedBlockOpen] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const pipelinePropertyId = project?.pipeline_property_id ?? null;

  // We still use useDealSettings here purely for the headline `repair_cost`
  // — it seeds the fallback-budget shown when a project has no line items
  // yet. The rehab scope itself no longer lives on property_setting.
  const { settings, isLoading: settingsLoading } =
    useDealSettings(pipelinePropertyId);

  // Line items query — scoped to the rehab_project_id so we only show rows
  // that have been claimed by this project (createProject sets that link).
  const { fetchLineItems, addLineItem, updateLineItem, deleteLineItem } =
    useRehab();
  const lineItemsKey = useMemo(
    () => ["rehab_line_items_project", project?.id] as const,
    [project?.id],
  );
  const lineItemsQuery = useQuery({
    queryKey: lineItemsKey,
    enabled: Boolean(project?.id),
    queryFn: () => (project?.id ? fetchLineItems(project.id) : Promise.resolve([])),
  });
  const invalidateLineItems = () => {
    void queryClient.invalidateQueries({ queryKey: lineItemsKey });
  };

  // Notes draft — autosaved on blur to avoid a network write per keystroke.
  const [notesDraft, setNotesDraft] = useState("");

  // Live drafts for the per-row "actual cost" inputs. Keyed by row id so
  // they survive any backend refetch. We overlay these on top of the
  // persisted rows so the Budget vs Actual chart updates on every keystroke
  // (for completed rows — pending ones don't count toward spend); the
  // network write happens when the field is committed (blur or Enter).
  const [actualDrafts, setActualDrafts] = useState<Record<string, number>>({});

  // Tracks whether any line-item edit was persisted this session. When the
  // drawer closes we only refetch the projects list (so the card behind it
  // reflects the new progress / budget) if something actually changed.
  const dirtyRef = useRef(false);

  // Reset notes + scrap any half-typed actuals whenever the drawer opens on
  // a new project, otherwise stale drafts would bleed across deals.
  useEffect(() => {
    if (open && project) {
      setNotesDraft(project.notes ?? "");
      setActualDrafts({});
      dirtyRef.current = false;
    }
  }, [open, project]);

  // Fallback budget for default-label scope when no rows exist yet — seeded
  // from property_setting.repair_cost so the user sees a starter scope.
  const fallbackBudget = useMemo(
    () => Number(settings.repair_cost ?? 0) || 0,
    [settings.repair_cost],
  );

  const baseItems: ScopeItem[] = useMemo(
    () =>
      composeScopeItems(
        (lineItemsQuery.data ?? []) as unknown as Parameters<
          typeof composeScopeItems
        >[0],
        fallbackBudget,
      ),
    [lineItemsQuery.data, fallbackBudget],
  );

  // Items as seen by every UI consumer in this drawer — persisted state with
  // any in-flight actual-cost edit overlaid on top. Both the chart and the
  // scope section read from this same array so they stay in lockstep.
  const items: ScopeItem[] = useMemo(
    () =>
      baseItems.map((it) =>
        it.id != null && actualDrafts[it.id] !== undefined
          ? { ...it, actual: actualDrafts[it.id] }
          : it,
      ),
    [baseItems, actualDrafts],
  );

  // Last known server value per row id — what a draft has to be diffed against
  // to decide whether a write is needed. `items` can't answer that: it already
  // has the in-flight draft overlaid, so comparing against it always reports
  // "unchanged" and the write never goes out.
  const persistedActualsRef = useRef<Record<string, number>>({});
  useEffect(() => {
    const map: Record<string, number> = {};
    for (const it of baseItems) if (it.id) map[it.id] = it.actual;
    persistedActualsRef.current = map;
  }, [baseItems]);

  if (!project) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full p-0 sm:max-w-[760px]" />
      </Sheet>
    );
  }

  const pd = (project.property_data ?? {}) as {
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    imageUrl?: string;
    strategy?: string;
  };
  const addr1 = pd.address ?? project.property_address ?? "Property";
  const addr2 =
    [pd.city, pd.state].filter(Boolean).join(", ") +
    (pd.zip ? ` ${pd.zip}` : "");
  const status = deriveStatus(project);

  // A property can't go into rehab while it's actively rented. Other listing
  // statuses (active/paused/draft/archived) — or no listing at all — don't block.
  const isRented = listings.some(
    (l) =>
      l.pipeline_property_id === project.pipeline_property_id &&
      l.status === "rented",
  );

  const handleStart = async () => {
    if (isRented) {
      setRentedBlockOpen(true);
      return;
    }
    await startProject(project.id);
  };

  // `in_rehab` gates editing. Parked projects (just moved to rehab, or moved
  // to Properties) are read-only — except a not-started project can still
  // have its scope planned (add/remove custom tasks) before it's started.
  const editable = project.in_rehab;
  const canManageCustom = editable || status === "not_started";

  // Progress % = share of completed scope items, counting only items with a
  // budget (the same set the task list / chart show), so an all-$0 default
  // scope doesn't dilute the percentage.
  const scoped = visibleScopeItems(items);
  const completedCount = scoped.filter((i) => i.done).length;
  const pct =
    scoped.length > 0
      ? Math.round((completedCount / scoped.length) * 100)
      : status === "completed"
        ? 100
        : status === "not_started"
          ? 0
          : 5;

  const handleToggleDone = async (item: ScopeItem, done: boolean) => {
    // Fallback rows have no DB id — they're synthesized for display only.
    if (!item.id) return;
    const rowId = item.id;
    // `item.actual` already reflects any in-flight draft (the `items` memo
    // overlays `actualDrafts`), so persist it alongside the status so a
    // typed-but-uncommitted amount isn't lost when the box is checked.
    let actualCost = item.actual;
    // Mirror the previous UX: auto-fill actual = budget when checking with
    // no prior actual recorded (the design did this through autoFillActual).
    if (done && (!actualCost || actualCost === 0) && item.budget > 0) {
      actualCost = item.budget;
    }
    // We're committing the actual now — drop any in-flight draft so the row
    // falls back to the canonical server value after the refetch, and record
    // what we just wrote so a follow-up blur doesn't repeat it.
    persistedActualsRef.current[rowId] = actualCost;
    setActualDrafts((prev) => {
      const copy = { ...prev };
      delete copy[rowId];
      return copy;
    });
    await updateLineItem(rowId, {
      status: done ? "completed" : "pending",
      actual_cost: actualCost,
      // Stamp the completion date when checking; clear it when reopening.
      // Preserve an already-set date so re-checking doesn't overwrite a
      // user-edited completion date with today.
      completed_at: done
        ? (item.completedAt ?? new Date().toISOString())
        : null,
    });
    dirtyRef.current = true;
    invalidateLineItems();
  };

  // Persist an edited completion date for an already-completed task. Clearing
  // the field (iso = null) leaves the task done but with no recorded date.
  const handleCompletionDateChange = async (
    item: ScopeItem,
    iso: string | null,
  ) => {
    if (!item.id) return;
    await updateLineItem(item.id, { completed_at: iso });
    dirtyRef.current = true;
    invalidateLineItems();
  };

  /**
   * Write one row's actual cost. Shared by the two commit paths (leaving the
   * field and pressing Enter), so an edit is persisted exactly once whichever
   * fires first: the server value is updated optimistically, and a repeat
   * commit of the same amount is a no-op.
   *
   * Returns false only when the write failed — the caller keeps the draft so
   * the typed amount stays on screen for a retry.
   */
  const persistActual = async (rowId: string, value: number) => {
    if (persistedActualsRef.current[rowId] === value) return true;
    const previous = persistedActualsRef.current[rowId];
    persistedActualsRef.current[rowId] = value;
    try {
      await updateLineItem(rowId, { actual_cost: value });
      dirtyRef.current = true;
      // Awaited (unlike the other mutations here) so the caller can drop the
      // draft only once the refetched row carries the new amount — dropping
      // it first would flash the pre-edit value back into the input.
      await queryClient.invalidateQueries({ queryKey: lineItemsKey });
      return true;
    } catch {
      // updateLineItem already surfaced the error toast. Roll the cached
      // server value back so committing again retries the write instead of
      // treating the failed amount as saved.
      persistedActualsRef.current[rowId] = previous;
      return false;
    }
  };

  // Live edit: update the draft only — no network write. A completed row's
  // new total reaches the chart on the next render via the `items` memo.
  const handleActualChange = (item: ScopeItem, value: number) => {
    if (!item.id) return;
    setActualDrafts((prev) => ({ ...prev, [item.id as string]: value }));
  };

  // Commit — fired when the field loses focus or the user presses Enter.
  // Writes the amount, then drops the draft so the row reads from the
  // canonical server state again. The draft is kept when the write failed
  // (the amount is still on screen to retry), and a newer draft — the user
  // typed again while the write was in flight — wins over the cleanup.
  const handleActualCommit = async (item: ScopeItem, value: number) => {
    if (!item.id) return;
    const rowId = item.id;
    const saved = await persistActual(rowId, value);
    if (!saved) return;
    setActualDrafts((prev) => {
      if (prev[rowId] !== value) return prev;
      const copy = { ...prev };
      delete copy[rowId];
      return copy;
    });
  };

  const handleAddCustom = async (label: string, estimate: number) => {
    if (!project?.id) return;
    await addLineItem(project.id, {
      category: "custom",
      label,
      pipeline_property_id: project.pipeline_property_id ?? null,
      estimated_cost: estimate,
      status: "pending",
    });
    dirtyRef.current = true;
    invalidateLineItems();
  };

  const handleDeleteCustom = async (item: ScopeItem) => {
    if (!item.isCustom || !item.id) return;
    await deleteLineItem(item.id);
    dirtyRef.current = true;
    invalidateLineItems();
  };

  const handleNotesBlur = async () => {
    if ((project.notes ?? "") === notesDraft) return;
    await updateProject(project.id, { notes: notesDraft });
    refresh();
  };

  // Wrap the parent's onOpenChange so closing the drawer after any line-item
  // edit refetches the projects list — the card behind it then shows the
  // updated progress / budget instead of the stale pre-edit figures.
  const handleOpenChange = (next: boolean) => {
    if (!next && dirtyRef.current) {
      dirtyRef.current = false;
      refresh();
    }
    onOpenChange(next);
  };

  const closeButton = (
    <button
      type="button"
      onClick={() => handleOpenChange(false)}
      className="flex-1 rounded-md border border-border bg-card px-3.5 py-2.5 text-sm font-bold text-foreground transition hover:border-border/80"
    >
      Close
    </button>
  );

  const startButton = (
    <button
      type="button"
      onClick={handleStart}
      className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-primary px-3.5 py-2.5 text-sm font-bold text-primary-foreground transition hover:brightness-110"
    >
      <Play className="h-3.5 w-3.5" aria-hidden /> Start Rehab
    </button>
  );

  // Footer is status-conditional (mirrors the design's drawer footer):
  //   - in_progress @ 100% → Close + "Mark Complete"
  //   - in_progress < 100% → no footer
  //   - not_started        → Close + "Start Rehab" (begins the project)
  //   - completed / parked → Close + "Start Rehab" (reopens the rehab)
  const footer = (() => {
    if (editable && status === "in_progress" && pct >= 100) {
      return (
        <>
          {closeButton}
          <button
            type="button"
            disabled={isCompleting}
            onClick={async () => {
              setIsCompleting(true);
              try {
                await completeProject(project.id);
              } finally {
                setIsCompleting(false);
              }
            }}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-success px-3.5 py-2.5 text-sm font-bold text-success-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Check className="h-3.5 w-3.5" aria-hidden />{" "}
            {isCompleting ? "Completing…" : "Mark Complete"}
          </button>
        </>
      );
    }
    // Active but under 100% — no transition available yet.
    if (editable) return null;
    // Parked (not_started or completed) — offer to (re)start the rehab.
    return (
      <>
        {closeButton}
        {startButton}
      </>
    );
  })();

  const scopeLoading = settingsLoading || lineItemsQuery.isLoading;

  return (
    <>
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        // Suppress the default top-right close — we render our own in the
        // bespoke header to match the design.
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[760px] [&>button:last-of-type]:hidden"
      >
        {/* Header */}
        <div className="flex items-start gap-3.5 border-b border-border px-6 py-5">
          <div
            className="h-[70px] w-[70px] flex-shrink-0 rounded-lg border border-border bg-muted/40 bg-cover bg-center"
            style={
              pd.imageUrl ? { backgroundImage: `url('${pd.imageUrl}')` } : undefined
            }
            aria-hidden
          />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <h2 className="truncate text-lg font-extrabold text-foreground">
              {addr1}
            </h2>
            {addr2 && (
              <div className="truncate text-sm text-muted-foreground">{addr2}</div>
            )}
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {pd.strategy && (
                <span className="rounded-full border border-border bg-card px-2 py-0.5 text-[11px] font-bold text-muted-foreground">
                  {pd.strategy}
                </span>
              )}
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-bold",
                  STATUS_CHIP[status],
                )}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
                {STATUS_LABEL[status]}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => handleOpenChange(false)}
            className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-md text-muted-foreground transition hover:bg-muted/40 hover:text-foreground"
            aria-label="Close drawer"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Progress overview */}
          <section className="mb-3.5 rounded-lg border border-border bg-muted/40 p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <TrendingUp className="h-3.5 w-3.5 text-primary" aria-hidden />
                Progress
              </span>
              <span className="text-xs font-bold tabular-nums text-muted-foreground">
                {pct}%
              </span>
            </div>
            <div className="flex flex-col gap-2">
              <div className="text-sm font-bold text-foreground">
                {STATUS_HEADLINE[status]}
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full border border-border bg-card">
                <div
                  className={cn(
                    "h-full rounded-full transition-all",
                    status === "completed"
                      ? "bg-gradient-to-r from-success to-success/60"
                      : "bg-gradient-to-r from-primary to-primary/60",
                  )}
                  style={{ width: `${pct}%` }}
                  aria-hidden
                />
              </div>
            </div>
          </section>

          {/* Key stats */}
          <section className="mb-3.5 rounded-lg border border-border bg-muted/40 p-4">
            <div className="mb-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <Hammer className="h-3.5 w-3.5 text-primary" aria-hidden />
              Key stats
            </div>
            <RehabStatsGrid project={project} items={items} />
          </section>

          {/* Budget vs Actual */}
          <section className="mb-3.5 rounded-lg border border-border bg-muted/40 p-4">
            <RehabBudgetChart items={items} />
          </section>

          {/* Scope & tasks */}
          <section className="mb-3.5 rounded-lg border border-border bg-muted/40 p-4">
            {scopeLoading ? (
              <div className="text-center text-xs text-muted-foreground">Loading…</div>
            ) : (
              <RehabScopeSection
                items={items}
                onToggleDone={handleToggleDone}
                onActualChange={handleActualChange}
                onActualCommit={handleActualCommit}
                onCompletionDateChange={handleCompletionDateChange}
                onAddCustom={handleAddCustom}
                onDeleteCustom={handleDeleteCustom}
                canEdit={editable}
                canManageCustom={canManageCustom}
              />
            )}
          </section>

          {/* Notes */}
          <section className="mb-1.5 rounded-lg border border-border bg-muted/40 p-4">
            <div className="mb-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <NotebookPen className="h-3.5 w-3.5 text-primary" aria-hidden />
              Notes
            </div>
            <Textarea
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              onBlur={handleNotesBlur}
              placeholder="Contractor updates, blockers, change orders..."
              className="min-h-[72px] resize-y"
            />
          </section>
        </div>

        {/* Footer (status-conditional) */}
        {footer && (
          <div className="flex gap-2.5 border-t border-border bg-card px-6 py-3.5">
            {footer}
          </div>
        )}
      </SheetContent>
    </Sheet>

    <AlertDialog open={rentedBlockOpen} onOpenChange={setRentedBlockOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>This property is currently rented</AlertDialogTitle>
          <AlertDialogDescription>
            You can't start a rehab while the unit has an active tenant.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li>
            <span className="font-semibold text-foreground">To rehab it:</span>{" "}
            first remove the property from rental on the Rentals page.
          </li>
          <li>
            <span className="font-semibold text-foreground">
              For minor repairs or upkeep:
            </span>{" "}
            use the Property Management section instead — no need to end the
            tenancy.
          </li>
        </ul>
        <AlertDialogFooter>
          <AlertDialogAction>Got it</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
